// src/components/SelectedWorkPreview.styles.ts
//
// Home "Selected work" section.
//
// The presentation system is built for real data that does not exist yet, so
// it is asymmetric by design: one project gets a large two-column featured
// slot, the rest fall into a two-up supporting row. That is deliberately not a
// three-card grid, and it degrades to a single column without reflowing.
//
// Nothing here can express a metric. There are no stat fields, no counts and
// no percentages, because no project data has been supplied and a number on
// this section would have to be invented.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const WorkRoot = styled.section`
  position: relative;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.background};
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const WorkContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

export const Header = styled(motion.header)`
  margin-bottom: 2.75rem;
`;

export const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

export const Lede = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* -------------------------------------------------------- featured slot */

export const Featured = styled(motion.article)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  /* Transform-only hover: lifting with transform cannot reflow the page. */
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.4s ease,
    box-shadow 0.4s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 0.2s ease;

    &:hover {
      transform: none;
    }
  }
`;

/* Visual slot. With no image yet it renders a quiet monospace placeholder
   rather than a broken <img>, and swaps to the real visual when one exists. */
export const FeaturedVisual = styled.div`
  position: relative;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media ${minDevice.laptop} {
    min-height: 100%;
    border-bottom: 0;
    border-right: 1px solid ${({ theme }) => theme.borderColor};
  }
`;

export const VisualPlaceholder = styled.span`
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  opacity: 0.6;
`;

export const FeaturedBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.75rem;

  @media ${minDevice.laptop} {
    padding: 2.25rem;
  }
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
`;

export const TypeBadge = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.headingColor};
`;

export const MetaDivider = styled.span`
  color: ${({ theme }) => theme.borderColor};
`;

export const CategoryText = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const ProjectTitle = styled.h3`
  margin: 0;
  font-size: 1.4rem;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 1.7rem;
  }
`;

export const ProjectDescription = styled.p`
  margin: 0.9rem 0 0;
  max-width: 54ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

export const ContextBlock = styled.p`
  margin: 0.9rem 0 0;
  max-width: 56ch;
  padding-left: 0.9rem;
  border-left: 2px solid ${({ theme }) => theme.borderColor};
  font-size: 0.95rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* Hairline tags rather than filled chips: the technology list needs to read as
   metadata, not as the primary content. */
export const TechList = styled.ul`
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const TechTag = styled.li`
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.7rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const ProjectLinks = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem;
`;

/* "View Project" on its own is ambiguous when several are on the page, so the
   project name is carried in visually hidden text. */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* View Project is the primary action; the repo/demo links are secondary and
   open in a new tab, so they carry an explicit rel. */
export const ViewLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.headingColor};
  text-decoration: none;
  border-bottom: 1px solid ${({ theme }) => theme.headingColor};
  padding-bottom: 0.2rem;
  transition: gap 0.25s ease, opacity 0.25s ease;

  &::after {
    content: '→';
    font-family: ${monoStack};
  }

  &:hover {
    gap: 0.75rem;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const ExternalLink = styled.a`
  font-family: ${monoStack};
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.25s ease, color 0.25s ease;

  &:hover {
    color: ${({ theme }) => theme.headingColor};
    border-bottom-color: ${({ theme }) => theme.borderColor};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* -------------------------------------------------- supporting work row */

export const MoreBlock = styled.div`
  margin-top: 2.5rem;
`;

export const MoreLabel = styled.p`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const SupportingGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;

  /* Two-up, never three-up: a three-up grid is exactly the stock portfolio
     layout this section is meant to avoid. */
  @media ${minDevice.laptop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SupportingCard = styled(motion.article)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.5rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.accent};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 0.2s ease;

    &:hover {
      transform: none;
    }
  }
`;

export const SupportingTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  line-height: 1.35;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};
`;

export const SupportingSummary = styled.p`
  margin: 0.6rem 0 0;
  font-size: 0.925rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------------------------------------------------------- empty state */

export const EmptyState = styled(motion.div)`
  padding: 2rem 1.75rem;
  border: 1px dashed ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.cardBg};

  @media ${minDevice.tablet} {
    padding: 2.75rem;
  }
`;

export const EmptyBadge = styled.p`  display: inline-block;
  margin: 0 0 1.1rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  line-height: 1.35;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 1.35rem;
  }
`;

export const EmptyBody = styled.p`
  margin: 0.85rem 0 0;
  max-width: 60ch;
  font-size: 0.975rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* --------------------------------------------------------------- footer */

export const CtaRow = styled(motion.div)`
  margin-top: 2.25rem;
`;

export const Cta = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0 0.35rem;
  background: none;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.headingColor};
  border-radius: 0;
  font-size: 0.975rem;
  font-weight: 600;
  color: ${({ theme }) => theme.headingColor};
  cursor: pointer;
  transition: gap 0.25s ease, border-color 0.25s ease;

  &::after {
    content: '→';
    font-family: ${monoStack};
  }

  &:hover {
    gap: 0.75rem;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
